
import React, {
useCallback,
useEffect,
useMemo,
useRef,
useState
} from "react";

import { useLocation,useNavigate } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import Layout from "../layouts/Layout";
import {
API_BASE_URL,
FONT_UI,
SKIN_THEMES
} from "../layouts/constants";


const DEFAULT_CENTER=[20.5937,78.9629];


function useSkin(){
const skinId=
localStorage.getItem("skinTheme")||"navy";

return (
SKIN_THEMES.find(
s=>s.id===skinId
)
|| SKIN_THEMES[0]
);
}


function api(path){
const base=API_BASE_URL.replace(/\/$/,"");

if(
base.endsWith("/api") &&
path.startsWith("/api/")
){
return `${base}${path.replace(/^\/api/,"")}`;
}

return `${base}${path}`;
}


function toNum(v){
const n=Number(v);
return Number.isFinite(n)?n:null;
}

function isNum(v){
return Number.isFinite(Number(v));
}

function km(m){
return (Number(m||0)/1000).toFixed(1);
}

function kph(mps){
return Math.round(
Number(mps||0)*3.6
);
}

function duration(ms){
if(!Number.isFinite(ms)||ms<0){
return "—";
}

const s=Math.floor(ms/1000);
const h=Math.floor(s/3600);
const m=Math.floor((s%3600)/60);
const sec=s%60;

if(h>0) return `${h}h ${m}m`;
if(m>0) return `${m}m ${sec}s`;
return `${sec}s`;
}


function vehicleSvg(){
return `
<svg viewBox="0 0 64 32" width="52" height="52"
xmlns="http://www.w3.org/2000/svg">
<rect x="2" y="10" width="30" height="12" rx="2" fill="#111827" />
<path d="M34 10h12l6 6v6H34z" fill="#111827" />
<circle cx="14" cy="26" r="4" fill="#1e293b" />
<circle cx="42" cy="26" r="4" fill="#1e293b" />
</svg>
`;
}


function vehicleIcon(){
return L.divIcon({
className:"escort-vehicle-icon",
html:vehicleSvg(),
iconSize:[52,52],
iconAnchor:[26,26]
});
}


export default function SentinelEscortLiveTracking(){

const navigate=useNavigate();
const location=useLocation();
const skin=useSkin();

const params=useMemo(
()=>new URLSearchParams(location.search),
[location.search]
);

const memberId=
params.get("member_id")||"";

const runId=
params.get("run_id")||"";


const [dashboardData,setDashboardData]=useState({
company_name:"",
user_map:""
});

const routePage=useMemo(
()=> dashboardData.user_map==="google"
?"/route-master"
:"/route-master1",
[dashboardData]
);

const [loading,setLoading]=useState(true);
const [pageError,setPageError]=useState("");

const mapRef=useRef(null);
const mapBoxRef=useRef(null);

const latestMarkerRef=useRef(null);
const startMarkerRef=useRef(null);
const stopMarkerRef=useRef(null);

const polyRef=useRef([]);
const latestLatLngRef=useRef(null);
const pollRef=useRef(null);
const fittedRef=useRef(false);


const [followLive,setFollowLive]=useState(true);
const followRef=useRef(true);

const [status,setStatus]=useState({
label:"—",
className:"bg-slate-200 text-slate-700"
});

const [meta,setMeta]=useState({
title:"—",
from:"—",
to:"—",
vehicle:"—",
lastTs:"—",
distance:"0",
speed:"0",
duration:"—",
points:"0"
});


const apiUrl=useMemo(()=>{

const q=new URLSearchParams({
member_id:memberId,
limit:"2000",
include_segments:"1"
});

if(runId){
q.set("run_id",runId);
}

return api(
`/api/sentinel_escort/live/tracking?${q.toString()}`
);

},[memberId,runId]);


const fetchDashboard=useCallback(async()=>{

try{

const token=
localStorage.getItem("token") ||
localStorage.getItem("authToken");

const res=await fetch(
api("/api/dashboard/admin/dashboard"),
{
headers:{
Authorization:`Bearer ${token}`
}
}
);

const json=await res.json();

setDashboardData(
json?.data?.companyDetails||{}
);

}
catch(e){
setPageError(e.message);
}
finally{
setLoading(false);
}

},[]);


useEffect(()=>{
fetchDashboard();
},[fetchDashboard]);


const clearMap=useCallback(()=>{

const map=mapRef.current;
if(!map) return;

if(latestMarkerRef.current){
map.removeLayer(
latestMarkerRef.current
);
latestMarkerRef.current=null;
}

if(startMarkerRef.current){
map.removeLayer(
startMarkerRef.current
);
startMarkerRef.current=null;
}

if(stopMarkerRef.current){
map.removeLayer(
stopMarkerRef.current
);
stopMarkerRef.current=null;
}

polyRef.current.forEach(
l=>map.removeLayer(l)
);

polyRef.current=[];

},[]);


const setRunStatus=useCallback(s=>{

s=String(s||"").toLowerCase();

if(s==="active"){
setStatus({
label:"Active",
className:"bg-green-100 text-green-700"
});
}
else if(
s==="stopped" ||
s==="abandoned"
){
setStatus({
label:s,
className:"bg-red-100 text-red-700"
});
}
else if(s==="planned"){
setStatus({
label:"Planned",
className:"bg-slate-200 text-slate-700"
});
}

},[]);


const drawTrack=useCallback(payload=>{

const map=mapRef.current;
if(!map) return;

clearMap();

const bounds=L.latLngBounds();

const points=
Array.isArray(payload.points)
?payload.points
:[];

const coords=points
.map(p=>[
toNum(p.lat),
toNum(p.lng)
])
.filter(
([a,b])=>isNum(a)&&isNum(b)
);

if(coords.length>=2){

const poly=L.polyline(
coords,
{
color:"#0284c7",
weight:5
}
).addTo(map);

polyRef.current.push(poly);

coords.forEach(
p=>bounds.extend(p)
);

}

const run=payload.run||{};


if(
isNum(run.start_lat)&&
isNum(run.start_lng)
){

startMarkerRef.current=
L.circleMarker(
[
run.start_lat,
run.start_lng
],
{
radius:8,
color:"green"
}
)
.addTo(map)
.bindTooltip(
run.from_name||"Start"
);

bounds.extend([
run.start_lat,
run.start_lng
]);

}


if(
isNum(run.end_lat)&&
isNum(run.end_lng)
){

stopMarkerRef.current=
L.circleMarker(
[
run.end_lat,
run.end_lng
],
{
radius:8,
color:"red"
}
)
.addTo(map)
.bindTooltip(
run.to_name||"End"
);

bounds.extend([
run.end_lat,
run.end_lng
]);

}


if(
payload.latest?.lat &&
payload.latest?.lng
){

const ll=L.latLng(
payload.latest.lat,
payload.latest.lng
);

latestLatLngRef.current=ll;

latestMarkerRef.current=
L.marker(ll,{
icon:vehicleIcon(),
zIndexOffset:999
})
.addTo(map)
.bindTooltip(
payload.latest.ts||"Live"
);

bounds.extend(ll);

if(followRef.current){
map.panTo(ll);
}

}

if(
bounds.isValid() &&
!fittedRef.current
){
map.fitBounds(
bounds.pad(0.15)
);
fittedRef.current=true;
}

},[clearMap]);


const refresh=useCallback(async()=>{

if(!memberId) return;

try{

const token=
localStorage.getItem("token")||
localStorage.getItem("authToken");

const res=await fetch(
apiUrl,
{
headers:{
Authorization:`Bearer ${token}`
}
}
);

const json=await res.json();

if(!res.ok || !json.success){
throw new Error(
json.message||"API error"
);
}

const payload=json.data;
const run=payload.run||{};
const latest=payload.latest||{};

setMeta({
title:run.title||"—",
from:run.from_name||"—",
to:run.to_name||"—",
vehicle:run.vehicle||"—",
lastTs:latest.ts||"—",
distance:km(run.distance_m),
speed:String(
kph(latest.speed_mps)
),
duration:
run.start_time
?
duration(
Date.parse(
run.end_time || new Date()
)-
Date.parse(
run.start_time
)
)
:"—",
points:String(
payload.total_points||0
)
});

setRunStatus(
run.status
);

drawTrack(payload);

}
catch(err){
console.error(err);
}

},[
apiUrl,
memberId,
setRunStatus,
drawTrack
]);


const fitTrack=()=>{
const map=mapRef.current;
if(!map) return;

const group=L.featureGroup([]);

if(latestMarkerRef.current){
group.addLayer(
latestMarkerRef.current
);
}

if(startMarkerRef.current){
group.addLayer(
startMarkerRef.current
);
}

if(stopMarkerRef.current){
group.addLayer(
stopMarkerRef.current
);
}

polyRef.current.forEach(
l=>group.addLayer(l)
);

const b=group.getBounds();

if(b.isValid()){
map.fitBounds(
b.pad(0.15)
);
}
};


const recenter=()=>{
if(
mapRef.current &&
latestLatLngRef.current
){
mapRef.current.panTo(
latestLatLngRef.current
);
}
};


useEffect(()=>{
followRef.current=followLive;
},[followLive]);


useEffect(()=>{

if(loading) return;
if(mapRef.current) return;

const map=L.map(
mapBoxRef.current
).setView(
DEFAULT_CENTER,
5
);

mapRef.current=map;

L.tileLayer(
"https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
{
attribution:"© OpenStreetMap"
}
).addTo(map);

return ()=>{
if(pollRef.current){
clearInterval(
pollRef.current
);
}
map.remove();
};

},[loading]);


useEffect(()=>{

if(loading) return;

refresh();

if(pollRef.current){
clearInterval(
pollRef.current
);
}

pollRef.current=
setInterval(
refresh,
12000
);

return ()=>{
if(pollRef.current){
clearInterval(
pollRef.current
);
}
};

},[
loading,
refresh
]);


if(loading){
return(
<div className="min-h-screen flex items-center justify-center">
Loading...
</div>
)
}


return(
<Layout
routePage={routePage}
dashboardData={dashboardData}
activePage="escort"
activeMenu="escort"
>

<style>{`
.leaflet-container{
height:100%;
width:100%;
}
.escort-vehicle-icon{
transform:translate(-50%,-50%);
}
`}</style>

{pageError && (
<div className="mb-4 p-3 rounded bg-red-50 text-red-700">
{pageError}
</div>
)}

<div className="bg-white rounded-2xl border shadow-sm overflow-hidden">

<div className="px-4 py-3 border-b flex gap-4 items-center flex-wrap">
<h3 className="font-bold text-base m-0">
Sentinel Escort Live Tracking
</h3>

<div>
Member:
<b> {memberId}</b>
</div>

{runId && (
<div>
Run #{runId}
</div>
)}

<button
onClick={()=>navigate('/escort-list')}
className="ml-auto px-4 py-2 rounded-xl border"
>
Escort List
</button>

<div className={`px-3 py-1 rounded-full text-xs font-bold ${status.className}`}>
{status.label}
</div>

</div>


<div className="px-4 py-3 flex gap-4 flex-wrap bg-slate-50 border-b">
<Info label="Title" value={meta.title}/>
<Info label="From" value={meta.from}/>
<Info label="To" value={meta.to}/>
<Info label="Vehicle" value={meta.vehicle}/>
<Info label="Last" value={meta.lastTs}/>
</div>


<div className="px-4 py-3 flex gap-3 flex-wrap border-b items-center">

<button
onClick={()=>setFollowLive(v=>!v)}
className="px-4 py-2 rounded-xl text-white"
style={{
background:skin.gradient
}}
>
Follow Live
</button>

<button
onClick={fitTrack}
className="px-4 py-2 rounded-xl border"
>
Fit Track
</button>

<button
onClick={recenter}
className="px-4 py-2 rounded-xl border"
>
Recenter
</button>

<div className="flex-1" />

<Info label="Distance" value={`${meta.distance} km`} />
<Info label="Speed" value={`${meta.speed} km/h`} />
<Info label="Duration" value={meta.duration} />
<Info label="Points" value={meta.points} />

</div>

<div
ref={mapBoxRef}
className="h-[calc(100vh-230px)] min-h-[550px]"
/>

</div>

</Layout>
)

}


function Info({label,value}){
return(
<div className="text-xs">
<span className="text-slate-500 mr-1">
{label}:
</span>
<b>{value||"—"}</b>
</div>
)
}
